import axios from "axios";
import {createContext, useEffect, useState} from "react";
import {v4 as uuidv4} from "uuid";
import { toast } from "react-toastify";

const LOGGED_OUT_TOAST_ID = "implicit-auth-logged-out";

export const StoreContext = createContext(null);

const StoreContextProvider = (props) => {
  // User details
  const [userId, setUserId] = useState();
  const [accessToken, setAccessToken] = useState();
  const [transientUser, setTransientUser] = useState(null);

  // Unique device ID
  const [deviceId, setDeviceId] = useState(null);

  useEffect(() => {
    let storedId = localStorage.getItem("deviceId");

    if (!storedId) {
      storedId = uuidv4(); // Generate new UUID
      localStorage.setItem("deviceId", storedId);
    }

    setDeviceId(storedId);
    console.log("uniqueId and deviceId",deviceId);
  }, []); // Runs only once when the component mounts

  

  const [requestId, setRequestId] = useState();

  const [orderId, setOrderId] = useState();
  const [offerData, setOfferData] = useState();
  const [roadId, setRoadId] = useState();
  const [tableId, setTableId] = useState();
  const [qrId, setQrId] = useState();
  const [restaurantData, setRestaurantData] = useState();
  const [cartData, setCartData] = useState({});
  const [prevRestaurantId, setPrevRestaurantId] = useState();
  const [showCheckout, setShowCheckout] = useState(false);
  const [paymentBreakup, setPaymentBreakup] = useState({});
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  // Set by a predecessor page (e.g. TrackBus) when it already knows/controls
  // the user's arrival time, so checkout should skip asking and use its
  // default instead of prompting. False unless a predecessor page opts in.
  const [useDefaultTimeToArrive, setUseDefaultTimeToArrive] = useState(false);

  // source and destination deatails
  const [source, setSource] = useState();
  const [destination, setDestination] = useState();

  // Fetch cart data on mount
  useEffect(() => {
    const fetchCart = async () => {
      try {
        const response = await axios.get(`/api/nexus/cart/fetch/v3/${deviceId}`);
        console.log("Fetched response:", response.data);

        if (response.data?.data) {
          // Transform v3 response to match existing cart data structure
          const cartItems = {};
          if (response.data.data.items) {
            response.data.data.items.forEach(item => {
              if (item.type === "BASIC") {
                cartItems[item.dishId] = {
                  count: item.count,
                  addOnItems: item.addOnItems || []
                };
              } else if (item.type === "VARIANT") {
                // For variants, create a nested structure
                cartItems[item.dishId] = {};
                if (item.variants) {
                  item.variants.forEach(variant => {
                    cartItems[item.dishId][variant.variantId] = {
                      count: variant.count,
                      addOnItems: variant.addOnItems || []
                    };
                  });
                }
              }
            });
          }
          
          setRoadId(response.data.data.positionGeohash);
          setPrevRestaurantId(response.data.data.restaurantId);
          setCartData(cartItems);
        } else {
          // Initialize with empty values if no cart data
          setRoadId(null);
          setPrevRestaurantId(null);
          setCartData({});
        }
      } catch (error) {
        console.log("Error fetching cart:", error);
        // Initialize with empty values on error
        setRoadId(null);
        setPrevRestaurantId(null);
        setCartData({});
      }
    };
    if (deviceId) {
      fetchCart();
    }
  }, [deviceId]);

  // Clear cart helper (used across flows)
  const clearCart = async () => {
    if (!deviceId) return;
    await axios.post("/api/nexus/cart/clear/device/" + deviceId);
    setCartData({});
    setPrevRestaurantId(null);
    setRoadId(null);
    setUseDefaultTimeToArrive(false);
  };

  // Function to add items to the cart
  const addToCart = async (dishId, restaurantId, roadid, variantId = null, addOnItems = [], quantity = 1) => {
    // Check if this is a different restaurant
    let baseCart = cartData;
    if (prevRestaurantId && prevRestaurantId !== restaurantId) {
      try {
        // Clear the existing cart first
        await clearCart();
        console.log("Cleared cart for different restaurant");
        // Use a clean cart to avoid mixing restaurants
        baseCart = {};
      } catch (error) {
        console.log("Error clearing cart:", error);
        return; // Don't proceed with adding new item if clearing failed
      }
    }

    // Create a new cart object with the updated quantity
    const updatedCart = { ...baseCart };
    
    if (variantId) {
      // Handle variant dish
      if (!updatedCart[dishId]) {
        updatedCart[dishId] = {};
      }
      if (updatedCart[dishId][variantId]) {
        updatedCart[dishId][variantId].count += quantity;
        // Update add-ons if provided, otherwise preserve existing add-ons
        if (addOnItems.length > 0) {
          updatedCart[dishId][variantId].addOnItems = addOnItems;
        }
        // If no new add-ons provided, keep existing add-ons (they're already there)
      } else {
        updatedCart[dishId][variantId] = {
          count: quantity,
          addOnItems: addOnItems
        };
      }
    } else {
      // Handle basic dish
      if (updatedCart[dishId]) {
        // Handle both old format (number) and new format (object)
        if (typeof updatedCart[dishId] === 'number') {
          updatedCart[dishId] = {
            count: updatedCart[dishId] + quantity,
            addOnItems: addOnItems
          };
        } else if (updatedCart[dishId] && updatedCart[dishId].count !== undefined) {
          updatedCart[dishId].count += quantity;
          // Update add-ons if provided, otherwise preserve existing add-ons
          if (addOnItems.length > 0) {
            updatedCart[dishId].addOnItems = addOnItems;
          }
          // If no new add-ons provided, keep existing add-ons (they're already there)
        } else {
          // Fallback for any other format
          updatedCart[dishId] = {
            count: quantity,
            addOnItems: addOnItems
          };
        }
      } else {
        updatedCart[dishId] = {
          count: quantity,
          addOnItems: addOnItems
        };
      }
    }

    if (!prevRestaurantId) {
      setPrevRestaurantId(restaurantId);
    }

    // Update local state immediately
    setCartData(updatedCart);

    // Transform cart data to v3 format
    const updatedDishes = [];
    Object.entries(updatedCart).forEach(([dishId, data]) => {
      if (typeof data === 'number') {
        // Handle legacy format (backward compatibility)
        const dishPayload = {
          type: "BASIC",
          dishId: dishId,
          count: data
        };
        updatedDishes.push(dishPayload);
      } else if (typeof data === 'object' && data !== null) {
        if (data.count !== undefined) {
          // Basic dish with new format
          const dishPayload = {
            type: "BASIC",
            dishId: dishId,
            count: data.count
          };
          
          // Add add-ons if present
          if (data.addOnItems && data.addOnItems.length > 0) {
            dishPayload.addOnItems = data.addOnItems;
          }

          updatedDishes.push(dishPayload);
        } else {
          // Variant dish
          const variants = Object.entries(data).map(([currentVariantId, variantData]) => {
            const variantPayload = {
              variantId: currentVariantId,
              count: variantData.count
            };

            // Add add-ons if present
            if (variantData.addOnItems && variantData.addOnItems.length > 0) {
              variantPayload.addOnItems = variantData.addOnItems;
            }

            return variantPayload;
          });

          updatedDishes.push({
            type: "VARIANT",
            dishId: dishId,
            variants: variants
          });
        }
      }
    });

    const requestData = {
      deviceId,
      positionGeohash: roadid,
      restaurantId,
      updatedDishes
    };

    try {
      const response = await axios.post("/api/nexus/cart/update/v3", requestData, {
        headers: { "Content-Type": "application/json" },
      });
      
      if (response.data?.data) {
        // Transform v3 response to match existing cart data structure
        const cartItems = {};
        if (response.data.data.items) {
          response.data.data.items.forEach(item => {
            if (item.type === "BASIC") {
              cartItems[item.dishId] = {
                count: item.count,
                addOnItems: item.addOnItems || []
              };
            } else if (item.type === "VARIANT") {
              cartItems[item.dishId] = {};
              if (item.variants) {
                item.variants.forEach(variant => {
                  cartItems[item.dishId][variant.variantId] = {
                    count: variant.count,
                    addOnItems: variant.addOnItems || []
                  };
                });
              }
            }
          });
        }
        
        setCartData(cartItems);
        setRoadId(response.data.data.positionGeohash);
        setPrevRestaurantId(response.data.data.restaurantId);
        
        // Start polling with exponential backoff
        pollCartWithBackoff(1);
      }
    } catch (error) {
      console.log("Error updating cart:", error);
      // Revert to previous state on error
      setCartData(cartData);
    }
  };

  // Function to add multiple items to cart in a single operation (for voice orders)
  const addMultipleToCart = async (items, restaurantId, roadid) => {
    // Check if this is a different restaurant
    let baseCart = cartData;
    if (prevRestaurantId && prevRestaurantId !== restaurantId) {
      try {
        // Clear the existing cart first
        await clearCart();
        console.log("Cleared cart for different restaurant");
        baseCart = {};
      } catch (error) {
        console.log("Error clearing cart:", error);
        throw error; // Don't proceed if clearing failed
      }
    }

    // Create a new cart object with all items
    const updatedCart = { ...baseCart };
    
    // Add all items to the cart
    for (const item of items) {
      const { dishId, count, variantId = null, addOnItems = [] } = item;
      
      if (variantId) {
        // Handle variant dish
        if (!updatedCart[dishId]) {
          updatedCart[dishId] = {};
        }
        if (updatedCart[dishId][variantId]) {
          updatedCart[dishId][variantId].count += count;
          if (addOnItems.length > 0) {
            updatedCart[dishId][variantId].addOnItems = addOnItems;
          }
        } else {
          updatedCart[dishId][variantId] = {
            count: count,
            addOnItems: addOnItems
          };
        }
      } else {
        // Handle basic dish
        if (updatedCart[dishId]) {
          if (typeof updatedCart[dishId] === 'number') {
            updatedCart[dishId] = {
              count: updatedCart[dishId] + count,
              addOnItems: addOnItems
            };
          } else if (updatedCart[dishId] && updatedCart[dishId].count !== undefined) {
            updatedCart[dishId].count += count;
            if (addOnItems.length > 0) {
              updatedCart[dishId].addOnItems = addOnItems;
            }
          } else {
            updatedCart[dishId] = {
              count: count,
              addOnItems: addOnItems
            };
          }
        } else {
          updatedCart[dishId] = {
            count: count,
            addOnItems: addOnItems
          };
        }
      }
    }

    if (!prevRestaurantId) {
      setPrevRestaurantId(restaurantId);
    }

    // Update local state immediately
    setCartData(updatedCart);

    // Transform cart data to v3 format
    const updatedDishes = [];
    Object.entries(updatedCart).forEach(([dishId, data]) => {
      if (typeof data === 'number') {
        // Handle legacy format (backward compatibility)
        updatedDishes.push({
          type: "BASIC",
          dishId: dishId,
          count: data
        });
      } else if (typeof data === 'object' && data !== null) {
        if (data.count !== undefined) {
          // Basic dish with new format
          updatedDishes.push({
            type: "BASIC",
            dishId: dishId,
            count: data.count,
            addOnItems: data.addOnItems || []
          });
        } else {
          // Variant dish
          const variants = Object.entries(data).map(([currentVariantId, variantData]) => ({
            variantId: currentVariantId,
            count: variantData.count,
            addOnItems: variantData.addOnItems || []
          }));

          updatedDishes.push({
            type: "VARIANT",
            dishId: dishId,
            variants: variants
          });
        }
      }
    });

    const requestData = {
      deviceId,
      positionGeohash: roadid,
      restaurantId,
      updatedDishes
    };

    try {
      const response = await axios.post("/api/nexus/cart/update/v3", requestData, {
        headers: { "Content-Type": "application/json" },
      });
      
      if (response.data?.data) {
        // Transform v3 response to match existing cart data structure
        const cartItems = {};
        if (response.data.data.items) {
          response.data.data.items.forEach(item => {
            if (item.type === "BASIC") {
              cartItems[item.dishId] = {
                count: item.count,
                addOnItems: item.addOnItems || []
              };
            } else if (item.type === "VARIANT") {
              cartItems[item.dishId] = {};
              if (item.variants) {
                item.variants.forEach(variant => {
                  cartItems[item.dishId][variant.variantId] = {
                    count: variant.count,
                    addOnItems: variant.addOnItems || []
                  };
                });
              }
            }
          });
        }
        
        setCartData(cartItems);
        setRoadId(response.data.data.positionGeohash);
        setPrevRestaurantId(response.data.data.restaurantId);
        
        // Start polling with exponential backoff
        pollCartWithBackoff(1);
      }
    } catch (error) {
      console.log("Error updating cart:", error);
      // Revert to previous state on error
      setCartData(cartData);
      throw error;
    }
  };

  // Function to remove items from the cart
  const removeFromCart = async (dishId, variantId = null, clearAllVariants = false) => {
    if (!cartData[dishId]) return;

    // Create a new cart object with the updated quantity
    const updatedCart = { ...cartData };
    
    if (clearAllVariants) {
      // Remove entire dish entry (all variants)
      delete updatedCart[dishId];
    } else if (variantId) {
      // Handle variant dish
      if (updatedCart[dishId] && updatedCart[dishId][variantId]) {
        updatedCart[dishId][variantId].count -= 1;
        if (updatedCart[dishId][variantId].count === 0) {
          delete updatedCart[dishId][variantId];
        }
        // Remove dish if no variants left
        if (Object.keys(updatedCart[dishId]).length === 0) {
          delete updatedCart[dishId];
        }
      }
    } else {
      // Handle basic dish
      if (typeof updatedCart[dishId] === 'number') {
        // Handle legacy format
        updatedCart[dishId] -= 1;
        if (updatedCart[dishId] === 0) {
          delete updatedCart[dishId];
        }
      } else if (updatedCart[dishId] && updatedCart[dishId].count !== undefined) {
        // Handle new format
        updatedCart[dishId].count -= 1;
        if (updatedCart[dishId].count === 0) {
          delete updatedCart[dishId];
        }
      }
    }

    // Update local state immediately
    setCartData(updatedCart);

    // Transform cart data to v3 format
    const updatedDishes = [];
    Object.entries(updatedCart).forEach(([dishId, data]) => {
      if (typeof data === 'number') {
        // Handle legacy format (backward compatibility)
        updatedDishes.push({
          type: "BASIC",
          dishId: dishId,
          count: data
        });
      } else if (typeof data === 'object' && data !== null) {
        if (data.count !== undefined) {
          // Basic dish with new format
          const dishPayload = {
            type: "BASIC",
            dishId: dishId,
            count: data.count
          };
          
          // Add add-ons if present
          if (data.addOnItems && data.addOnItems.length > 0) {
            dishPayload.addOnItems = data.addOnItems;
          }

          updatedDishes.push(dishPayload);
        } else {
          // Variant dish
          const variants = Object.entries(data).map(([currentVariantId, variantData]) => {
            const variantPayload = {
              variantId: currentVariantId,
              count: variantData.count
            };

            // Add add-ons if present
            if (variantData.addOnItems && variantData.addOnItems.length > 0) {
              variantPayload.addOnItems = variantData.addOnItems;
            }

            return variantPayload;
          });

          updatedDishes.push({
            type: "VARIANT",
            dishId: dishId,
            variants: variants
          });
        }
      }
    });

    const requestData = {
      deviceId,
      positionGeohash: roadId,
      restaurantId: prevRestaurantId,
      updatedDishes
    };

    try {
      const response = await axios.post(
        "/api/nexus/cart/update/v3",
        requestData,
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      
      if (response.data?.data) {
        // Transform v3 response to match existing cart data structure
        const cartItems = {};
        if (response.data.data.items) {
          response.data.data.items.forEach(item => {
            if (item.type === "BASIC") {
              cartItems[item.dishId] = item.count;
            } else if (item.type === "VARIANT") {
              cartItems[item.dishId] = {};
              if (item.variants) {
                item.variants.forEach(variant => {
                  cartItems[item.dishId][variant.variantId] = variant.count;
                });
              }
            }
          });
        }
        
        setCartData(cartItems);
        setRoadId(response.data.data.positionGeohash);
        setPrevRestaurantId(response.data.data.restaurantId);
        
        // Start polling with exponential backoff
        pollCartWithBackoff(1);
      }
    } catch (error) {
      console.log("Error updating cart:", error);
      // Revert to previous state on error
      setCartData(cartData);
    }
  };

  // Exponential backoff polling function
  const pollCartWithBackoff = async (attempt) => {
    if (attempt > 5) return; // Stop after 5 attempts
    
    const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000); // Max 10 seconds
    
    try {
      await new Promise(resolve => setTimeout(resolve, delay));
      
      const response = await axios.get(`/api/nexus/cart/fetch/v3/${deviceId}`);
      if (response.data?.data) {
        // Transform v3 response to match existing cart data structure
        const cartItems = {};
        if (response.data.data.items) {
          response.data.data.items.forEach(item => {
            if (item.type === "BASIC") {
              cartItems[item.dishId] = {
                count: item.count,
                addOnItems: item.addOnItems || []
              };
            } else if (item.type === "VARIANT") {
              cartItems[item.dishId] = {};
              if (item.variants) {
                item.variants.forEach(variant => {
                  cartItems[item.dishId][variant.variantId] = {
                    count: variant.count,
                    addOnItems: variant.addOnItems || []
                  };
                });
              }
            }
          });
        }
        
        setCartData(cartItems);
        setRoadId(response.data.data.positionGeohash);
        setPrevRestaurantId(response.data.data.restaurantId);
        
        // Continue polling if there are items
        if (Object.keys(cartItems).length > 0) {
          pollCartWithBackoff(attempt + 1);
        }
      }
    } catch (error) {
      console.log("Error polling cart:", error);
      // Continue polling on error
      pollCartWithBackoff(attempt + 1);
    }
  };

  // Fetch restaurants dynamically based on URL parameters
  useEffect(() => {
    const fetchRestaurants = async () => {
      console.log("s d checking",source,destination)
      if (!source || !destination) {
        console.log("Missing source or destination in query parameters.");
        return;
      }
      console.log("from context", source, destination);

      const [sourceLat, sourceLng] = source
        .split(",")
        .map((coord) => coord.trim());
      const [destinationLat, destinationLng] = destination
        .split(",")
        .map((coord) => coord.trim());

      try {
        const requestData = {
          source: { lat: parseFloat(sourceLat), lng: parseFloat(sourceLng) },
          destination: {
            lat: parseFloat(destinationLat),
            lng: parseFloat(destinationLng),
          },
          filters: [{ type: "OPERATION_STATUS", value: "ONLINE" }],
          pageStart: 0,
        };

        const response = await axios.post(
          "/api/catalogue/restaurant/suggest",
          requestData,
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        setRestaurantData(response.data.restaurants);
        console.log("Fetched restaurant data:", response.data.restaurants);
      } catch (err) {
        console.error("Error fetching restaurants:", err);
      }
    };

    fetchRestaurants();
  }, [window.location.search]); // Runs when URL parameters change

  // Retrieve userId and auth-token using sessionId
  const userDetails = async (requestIdOverride) => {
    const effectiveRequestId = requestIdOverride ?? requestId;
    const cleanedRequestId =
      effectiveRequestId === undefined || effectiveRequestId === null
        ? ""
        : String(effectiveRequestId).trim();
    if (!cleanedRequestId || cleanedRequestId === "undefined" || cleanedRequestId === "null") {
      return null;
    }

    console.log("Authenticating implicit with id:", cleanedRequestId);
    try {
      const requestData = { requestId: cleanedRequestId };
      const response = await axios.post(
        "/api/userservice/authentication/authenticate/implicit",
        requestData,
        {
          headers: {
            accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      console.log("responce for retrieve userId and auth token:", response)
      const data = response?.data?.data;
      if (data) {
        console.log(response.data);
        console.log(data);
        setUserId(data.userId);
        setAccessToken(data.accessToken);
        setTransientUser(
          data.transientUser ?? data.transient ?? false
        );
        console.log(
          "UserId & AccessToken : ",
          data.userId,
          data.accessToken
        );
        return data;
      } else {
        console.log("Error in Retrieving user details");
        throw new Error("Failed to retrieve user details");
      }
    } catch (error) {
      const status = error?.response?.status;
      const responseData = error?.response?.data;
      console.log("Implicit auth error:", status, responseData || error?.message);
      if (status === 400) {
        try {
          if (!toast.isActive(LOGGED_OUT_TOAST_ID)) {
            toast.info("You have been logged out, but can still see the menu!", {
              toastId: LOGGED_OUT_TOAST_ID,
              position: "top-center",
              autoClose: 4000,
            });
          }
        } catch (e) {
          // ignore toast failures
          console.error("Failed to open the logged-out toast")
        }
        // IMPORTANT: menu is allowed for guest users; do not crash the app on implicit auth 400.
        // Treat as logged out and continue.
        return null;
      }
      throw error;
    }
  };

  useEffect(() => {
    console.log(
      "useEffect triggered storeContext : source=",
      source,
    );
    if (requestId) {
      // Best-effort auth: do not crash app if auth fails (e.g., 400 logged out)
      userDetails().catch(() => null);
    }
  }, [source, requestId]); // ✅ Now waits for requestId

  // Context value provided to components
  const contextValue = {
    source,
    setSource,
    destination,
    setDestination,
    cartData,
    setCartData,
    addToCart,
    addMultipleToCart,
    removeFromCart,
    userId,
    setUserId,
    accessToken,
    setAccessToken,
    transientUser,
    prevRestaurantId,
    setRoadId,
    deviceId,
    restaurantData,
    setRestaurantData,
    roadId,
    orderId,
    setOrderId,
    offerData,
    setOfferData,
    showCheckout,
    setShowCheckout,
    paymentBreakup,
    setPaymentBreakup,
    isBottomSheetOpen,
    setIsBottomSheetOpen,
    requestId,
    setRequestId,
    userDetails,
    tableId,
    setTableId,
    qrId,
    setQrId,
    clearCart,
    useDefaultTimeToArrive,
    setUseDefaultTimeToArrive,
  };

  return (
    <StoreContext.Provider value={contextValue}>
      {props.children}
    </StoreContext.Provider>
  );
};

export default StoreContextProvider;
